import { resolveAspectRatio } from "../components/AspectRatio";
import { PLAYER_TYPE, PlayerUiType, State } from "../enums";
import { getAllFromStorage, PersistedStateName } from "../helpers/persist";
import {
  getStartTime,
  isAudio,
  isChannelPlayer,
  isContinuingPlayback,
  isFakeOndemand,
  isLivePlayer
} from "../helpers/previewHelpers";
import { PlayerEmbedParams } from "../types/common";
import { Media } from "../types/player-api";

import {
  PLAYER_STORE_DEFAULTS,
  PlayerState,
  PlayerStoreProperties
} from "./store";

const getPlayerUiType = (embedParams: PlayerEmbedParams): PlayerUiType => {
  if (embedParams.backgroundVideo) {
    // BackgroundVideo param overrides the deprecated controls and playerUi params
    return PlayerUiType.backgroundVideo;
  }

  if (!embedParams.playerUi && !embedParams.controls) {
    return PlayerUiType.off;
  }
  if (!embedParams.playerUi && embedParams.controls) {
    return PlayerUiType.backgroundVideo;
  }
  if (embedParams.playerUi && !embedParams.controls) {
    return PlayerUiType.hiddenControls;
  }

  return PlayerUiType.default;
};

/**
 * Set initial state once when player is embedded
 * @param embedParams user provided embed params
 * @param initialState optional initial state, used for testing
 */
export const getInitialState = (
  embedParams: PlayerEmbedParams,
  initialState: Partial<PlayerStoreProperties> = {}
) => {
  const isMobile = PLAYER_STORE_DEFAULTS.device.isMobile;
  const persistedValues = getAllFromStorage();
  const volume =
    persistedValues[PersistedStateName.VOLUME] ?? PLAYER_STORE_DEFAULTS.volume;
  return {
    ...PLAYER_STORE_DEFAULTS,
    aspectRatio: embedParams.aspectRatio,
    isAutoplay: embedParams.autoplay,
    isChatButtonEnabled:
      embedParams.chatButton ?? PLAYER_STORE_DEFAULTS.isChatButtonEnabled,
    isControlsVisible:
      !isMobile || embedParams.autoControls || embedParams.autoplay,
    isMuted:
      embedParams.autoMute ||
      persistedValues[PersistedStateName.MUTED] ||
      volume === 0 ||
      PLAYER_STORE_DEFAULTS.isMuted,
    isPlaybackStarted: embedParams.autoplay,
    playbackRate:
      embedParams.playbackRate || PLAYER_STORE_DEFAULTS.playbackRate,
    playerUiType: getPlayerUiType(embedParams),
    subtitlesBackground:
      persistedValues[PersistedStateName.SUBTITLES_BACKGROUND] ||
      embedParams.subtitlesBackground ||
      PLAYER_STORE_DEFAULTS.subtitlesBackground,
    subtitlesSizeMultiplier:
      persistedValues[PersistedStateName.SUBTITLES_SIZE] ||
      PLAYER_STORE_DEFAULTS.subtitlesSizeMultiplier,
    userPreferredAudioTrack:
      persistedValues[PersistedStateName.USER_PREFERED_AUDIO_TRACK],
    userPreferredSubtitles:
      persistedValues[PersistedStateName.USER_PREFERRED_SUBTITLES],
    userPreferredVideoTrack:
      persistedValues[PersistedStateName.USER_PREFERRED_VIDEO_TRACK],
    volume,
    ...initialState
  };
};

const resolvePublicationType = state => {
  switch (state) {
    case State.ONGOING_ONDEMAND:
      return "OnDemandPublication";
    case State.ONGOING_EVENT:
      return "ScheduledTransmission";
    default:
      return undefined;
  }
};

/**
 * Set store state each time a media is changed
 */
export const getStoreStateOnMediaChanged = ({
  aspectRatio,
  data,
  embedId,
  embedParams,
  isAutoplay,
  isFirstTimeRender,
  playerType,
  state,
  userData
}: {
  aspectRatio?: string;
  data: Media;
  embedId: string;
  embedParams: PlayerEmbedParams;
  isAutoplay: boolean;
  isFirstTimeRender: boolean;
  playerType: PLAYER_TYPE;
  state: State;
  userData?: {
    watchedDuration?: number;
  };
}): Partial<PlayerState> => {
  const isContinuingWatching =
    embedParams.playbackHistory &&
    isContinuingPlayback(
      data.duration?.duration_in_seconds,
      userData?.watchedDuration,
      embedParams.seek
    );

  const updatedStoreValues: Partial<PlayerState> = {
    aspectRatio: resolveAspectRatio(
      aspectRatio || embedParams.aspectRatio,
      data.streamAspectRatio
    ),
    audioTracks: PLAYER_STORE_DEFAULTS.audioTracks,
    buffered: undefined,
    contentType: data.contentType,
    currentTime: getStartTime(
      isFirstTimeRender ? embedParams.seek : 0, // Seek affects only to first time render
      isContinuingWatching,
      userData?.watchedDuration
    ),
    durationInSeconds: data.duration?.duration_in_seconds,
    dvrWindow: data?.dvrWindowInSeconds,
    embedId,
    isAdaptiveBitrate: PLAYER_STORE_DEFAULTS.isAdaptiveBitrate,
    isAudio: embedParams.yleAudioPlayer || isAudio(data.contentType),
    isAutoplay,
    isContinuingWatching,
    isDestroyed: false,
    isEnded: false,
    isFakeOnDemand: isFakeOndemand(state, data.serviceSchedule),
    isLive: isLivePlayer(playerType),
    isLoading: false,
    isSeeking: isLivePlayer(playerType),
    isSimulcast: isChannelPlayer(playerType),
    media: data,
    mediaId: data.mediaId,
    programId: data.programId,
    publicationId: data.publicationId,
    publicationType: resolvePublicationType(state),
    seekableEnd: data.duration?.duration_in_seconds,
    selectedAudioTrackId: PLAYER_STORE_DEFAULTS.selectedAudioTrackId,
    selectedSubtitles: PLAYER_STORE_DEFAULTS.selectedSubtitles,
    selectedVideoTrack: PLAYER_STORE_DEFAULTS.selectedVideoTrack,
    serviceId: data.serviceId,
    subtitles: PLAYER_STORE_DEFAULTS.subtitles,
    subtitlesCue: PLAYER_STORE_DEFAULTS.subtitlesCue,
    videoTracks: PLAYER_STORE_DEFAULTS.videoTracks
  };

  if (!isAutoplay) {
    updatedStoreValues.isPlaybackStarted = false;
  }

  return updatedStoreValues;
};
